import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyInput, parseMoney } from "@/components/MoneyInput";
import { api, formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { FAMILY_SUBTYPES, SUBTYPE_LABELS, subsFor, type Account } from "@/lib/finance";
import { Pick } from "@/components/movements/TransactionFormDialog";
import { useInlineAccount } from "@/components/accounts/useInlineAccount";
import type { NewLoan } from "@/components/accounts/LoanBudgetPrompt";

const EMPTY = { name: "", subtype: "house", date: new Date().toISOString().slice(0, 10), total: "", cash: "", source: "", useLoan: false,
  loanName: "", loanType: "mortgage", institution: "", monthly: "", payDay: "" };
const LOAN_TYPES: [string, string][] = [["mortgage", "Hipotecario"], ["auto", "Automotriz"], ["personal", "Personal"]];
const n = (v: string) => (v.trim() ? parseMoney(v) : 0);

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1.5"><Label className="text-sm text-slate-700">{label}</Label>{children}</div>;
}

export function AssetPurchaseWizard({ open, onOpenChange, debitAccounts: debitProp, onSaved }: { open: boolean; onOpenChange: (o: boolean) => void; debitAccounts: Account[]; onSaved: (loan?: NewLoan) => void }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof EMPTY) => (v: string | boolean) => setF((s) => ({ ...s, [k]: v }));
  const { all: debitAccounts, create, dialog } = useInlineAccount(debitProp);
  useEffect(() => { if (open) setF({ ...EMPTY, source: debitProp[0]?.id ?? "" }); }, [open, debitProp]);
  const financed = Math.max(n(f.total) - n(f.cash), 0);
  const save = async () => {
    if (!f.name.trim() || n(f.total) <= 0) return toast.error("Escribe qué compras y su valor total");
    setBusy(true);
    try {
      const loanName = f.loanName.trim() || `Crédito ${f.name.trim()}`;
      const { data } = await api.post<{ loan_id: string | null }>("/assets/purchase", {
        name: f.name.trim(), account_subtype: f.subtype, date: f.date, total: n(f.total), cash_amount: n(f.cash),
        source_account_id: n(f.cash) > 0 ? f.source : null,
        loan: f.useLoan && financed > 0 ? { name: loanName, account_subtype: f.loanType, institution: f.institution.trim() || null,
          loan_monthly_payment: n(f.monthly) || null, payment_day: f.payDay ? Number(f.payDay) : null } : null,
      });
      toast.success("Bien registrado");
      onOpenChange(false);
      onSaved(data.loan_id && n(f.monthly) > 0 ? { id: data.loan_id, name: loanName, monthly: n(f.monthly) } : undefined);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="asset-purchase-wizard">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1B2A4A]">Comprar un bien</DialogTitle>
          <DialogDescription>Los gastos de la compra (notario, escrituras, impuestos, comisión) regístralos aparte como Gasto.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Row label="¿Qué compras?"><Input value={f.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("name")(e.target.value)} placeholder="Ej. Departamento Roma" className="h-11" data-testid="wizard-name" /></Row>
            <Row label="Tipo">
              <Select value={f.subtype} onValueChange={(v: string) => { set("subtype")(v); set("loanType")(v === "vehicle" ? "auto" : "mortgage"); }}>
                <SelectTrigger className="h-11" data-testid="wizard-subtype"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{FAMILY_SUBTYPES.property.map((s) => <SelectItem key={s} value={s}>{SUBTYPE_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Valor total"><MoneyInput id="wizard-total" value={f.total} onChange={set("total")} className="h-11" testId="wizard-total" /></Row>
            <Row label="Fecha de compra"><Input type="date" value={f.date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("date")(e.target.value)} className="h-11" data-testid="wizard-date" /></Row>
            <Row label="Pagas de contado"><MoneyInput id="wizard-cash" value={f.cash} onChange={set("cash")} className="h-11" testId="wizard-cash" /></Row>
            <Pick label="Desde la cuenta" value={f.source} onChange={set("source")} testId="wizard-source" placeholder="Cuenta de débito"
              options={debitAccounts.map((a) => ({ value: a.id, label: a.name }))} onCreate={() => create(subsFor(["debit"]), (a) => set("source")(a.id))} />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl bg-[#FAF7F1] p-4">
            <div><p className="text-sm font-semibold text-[#1B2A4A]">¿Usas crédito?</p><p className="text-xs text-slate-500">Monto del crédito: <b className="tabular" data-testid="wizard-financed">{money(financed)}</b></p></div>
            <Switch checked={f.useLoan} onCheckedChange={set("useLoan")} data-testid="wizard-use-loan" />
          </div>
          {f.useLoan && (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="wizard-loan-form">
              <Row label="Nombre del crédito"><Input value={f.loanName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("loanName")(e.target.value)} className="h-11" data-testid="wizard-loan-name" /></Row>
              <Row label="Tipo de crédito">
                <Select value={f.loanType} onValueChange={set("loanType")}>
                  <SelectTrigger className="h-11" data-testid="wizard-loan-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{LOAN_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Row>
              <Row label="Institución"><Input value={f.institution} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("institution")(e.target.value)} className="h-11" data-testid="wizard-loan-institution" /></Row>
              <Row label="Mensualidad"><MoneyInput id="wizard-monthly" value={f.monthly} onChange={set("monthly")} className="h-11" testId="wizard-loan-monthly" /></Row>
              <Row label="Día de pago"><Input inputMode="numeric" value={f.payDay} placeholder="1 a 31" onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("payDay")(e.target.value.replace(/\D/g, "").slice(0, 2))} className="h-11" data-testid="wizard-loan-payday" /></Row>
            </div>
          )}
          <Button onClick={save} disabled={busy} className="h-11 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="wizard-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar compra"}
          </Button>
        </div>
      </DialogContent>
      {dialog}
    </Dialog>
  );
}
